import { LmsSourceQuestion, type LmsSourceQuestionSnapshot } from './LmsSourceQuestion';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useBlocker } from 'react-router-dom';
import { LearningDemoEntry } from '@/pages/lms-demo/LearningDemoEntry';
import { api } from '@/lib/api';
import { getKidSub } from '@/auth/authStore';
import { AssignmentPhotoPicker } from './AssignmentPhotoPicker';
const bodySchema = z.object({
  text: z.string().max(12000),
  attachment_ids: z.array(z.string()).max(8),
  project_ids: z.array(z.string()).max(3),
  acknowledge: z.boolean().refine((v) => v, 'Confirm who can see this work.'),
});
type FormValues = z.infer<typeof bodySchema>;
interface WorkBody {
  text?: string;
  attachment_ids?: string[];
  project_ids?: string[];
  project_versions?: Array<{ title: string; version: number }>;
}
interface Review {
  id: string;
  version: number;
  action: string;
  result: { points?: number; level?: string };
  scheme: { kind: string; maximum?: number };
  feedback: string;
  created_at: string;
}
interface Revision {
  id: string;
  number: number;
  body: WorkBody;
  late: boolean;
  submitted_at: string;
  reviews: Review[];
}
interface ResponseData {
  excusal?: { id: string; reason: string } | null;
  assignment: {
    id: string;
    title: string;
    instructions: string;
    status: string;
    due_at: string;
    config: {
      scheme: { kind: string; criteria: string[]; maximum?: number; levels?: string[] };
      response_types: string[];
      allow_resubmit: boolean;
      material_snapshot?: {
        questions?: LmsSourceQuestionSnapshot[];
        lesson?: { title: string } | null;
        materials?: Array<{ title: string; url: string }>;
      };
    };
  };
  submission: { version: number; state: string; draft: WorkBody } | null;
  revisions: Revision[];
  files: Array<{ id: string; name: string; mime: string; size: number }>;
  visibility_notice: string;
}
const errorText = (e: unknown) =>
  e instanceof Error ? e.message : 'The action could not be saved. Keep your draft and try again.';
export function LmsAssignmentResponse({
  assignmentId,
  request = api,
  kidId = getKidSub(),
}: {
  assignmentId: string;
  request?: typeof api;
  kidId?: string | null;
}) {
  const data = useQuery({
    queryKey: ['lms-student', assignmentId, kidId],
    queryFn: () => request<ResponseData>(`/study/lms/assignments/${assignmentId}`),
    retry: false,
  });
  return (
    <section className="mt-6 space-y-5">
      {request === api && <LearningDemoEntry perspective="student" />}
      {data.isLoading && <p role="status">Loading your assignment…</p>}
      {data.isError && (
        <section className="card-base">
          <p role="alert">{errorText(data.error)}</p>
          <button className="btn-pill-secondary" onClick={() => void data.refetch()}>
            Try again
          </button>
        </section>
      )}
      {data.data && !data.isError && (
        <ResponseForm
          key={`${assignmentId}:${kidId}`}
          data={data.data}
          request={request}
          kidId={kidId}
        />
      )}
    </section>
  );
}
function ResponseForm({
  data,
  request,
  kidId,
}: {
  data: ResponseData;
  request: typeof api;
  kidId: string | null;
}) {
  const id = data.assignment.id,
    client = useQueryClient(),
    observed = useRef(data.submission?.version ?? 0),
    key = useRef(crypto.randomUUID()),
    [saved, setSaved] = useState(''),
    [image, setImage] = useState<{ mime: string; data_base64: string; revisionId: string } | null>(
      null,
    ),
    [privateImage, setPrivateImage] = useState<{ mime: string; data_base64: string } | null>(null);
  const defaults: FormValues = {
    text: data.submission?.draft.text ?? '',
    attachment_ids: data.submission?.draft.attachment_ids ?? [],
    project_ids: data.submission?.draft.project_ids ?? [],
    acknowledge: true,
  };
  const form = useForm<FormValues>({
    resolver: zodResolver(bodySchema),
    defaultValues: { ...defaults, acknowledge: false },
  });
  const dirty = form.formState.isDirty;
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm('Leave without saving your response changes?')) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker]);
  useEffect(() => {
    const version = data.submission?.version ?? 0;
    if (version > observed.current && !dirty) {
      observed.current = version;
      form.reset({
        text: data.submission?.draft.text ?? '',
        attachment_ids: data.submission?.draft.attachment_ids ?? [],
        project_ids: data.submission?.draft.project_ids ?? [],
        acknowledge: false,
      });
      key.current = crypto.randomUUID();
    }
  }, [data.submission, dirty, form]);
  const [photoWarning, setPhotoWarning] = useState('');
  const uploads = useRef(new WeakMap<File, Map<number, string>>());
  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);
  const write = useMutation({
    mutationFn: ({ values, final }: { values: FormValues; final: boolean }) =>
      request<{ version: number; demo?: boolean }>(
        `/study/lms/assignments/${id}/${final ? 'submit' : 'draft'}`,
        {
          method: final ? 'POST' : 'PUT',
          body: {
            expected_version: observed.current,
            body: {
              text: values.text,
              attachment_ids: values.attachment_ids,
              project_ids: values.project_ids,
            },
            acknowledge_visibility: values.acknowledge,
            request_key: key.current,
          },
        },
      ),
    onSuccess: async (result, variables) => {
      observed.current = result.version;
      form.reset(variables.values);
      setSaved(
        result.demo
          ? variables.final
            ? 'Demo response submitted locally. No teacher or school receives it.'
            : 'Demo draft saved in memory. Reload resets this example.'
          : variables.final
            ? 'Work submitted. Your teacher can review this saved version.'
            : 'Draft saved. It is still private until you submit.',
      );
      key.current = crypto.randomUUID();
      await client.invalidateQueries({ queryKey: ['lms-student', id] });
      await client.invalidateQueries({ queryKey: ['study-assignments'] });
    },
  });
  const upload = useMutation({
    mutationFn: async ({ file, rotation }: { file: File; rotation: number }) => {
      if (form.getValues('attachment_ids').length >= 8)
        throw new Error('Remove a photo before adding another. A response holds up to 8 photos.');
      const body = new FormData();
      body.append('file', file);
      const keys = uploads.current.get(file) ?? new Map<number, string>();
      const token = keys.get(rotation) ?? crypto.randomUUID();
      keys.set(rotation, token);
      uploads.current.set(file, keys);
      body.append('rotation', String(rotation));
      body.append('request_key', token);
      return request<{ id: string; warnings?: string[] }>(`/study/lms/assignments/${id}/images`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: async (r) => {
      setPhotoWarning(
        r.warnings?.length
          ? 'This photo may be blurry or too small. Open Preview and check every line before submitting; remove and retake if needed.'
          : '',
      );
      key.current = crypto.randomUUID();
      form.setValue(
        'attachment_ids',
        Array.from(new Set([...form.getValues('attachment_ids'), r.id])),
        { shouldDirty: true },
      );
      await client.invalidateQueries({ queryKey: ['lms-student', id] });
    },
  });
  const projects = useQuery({
    queryKey: ['lms-own-projects', kidId],
    queryFn: () =>
      request<Array<{ id: string; title: string; vfs_version?: number }>>(
        `/kids/${kidId}/projects`,
      ),
    enabled: Boolean(kidId) && data.assignment.config.response_types.includes('project'),
    retry: false,
  });
  const canEdit =
    data.assignment.status === 'published' &&
    (!data.submission || ['draft', 'returned'].includes(data.submission.state));
  return (
    <>
      <header className="card-base space-y-3">
        <p className="eyebrow eyebrow-sky">From your teacher</p>
        <h1 className="section-heading">{data.assignment.title}</h1>
        <p>
          Due {new Date(data.assignment.due_at).toLocaleString()} · {data.assignment.status}
        </p>
        <p className="whitespace-pre-wrap">{data.assignment.instructions}</p>
        {data.assignment.config.material_snapshot?.questions?.map((q) => (
          <LmsSourceQuestion
            key={q.id}
            question={q}
            assignmentId={data.assignment.id}
            request={request}
          />
        ))}
        {data.assignment.config.material_snapshot?.lesson && (
          <p>Related lesson: {data.assignment.config.material_snapshot.lesson.title}</p>
        )}
        {data.assignment.config.material_snapshot?.materials?.map((m, i) => (
          <a
            className="block underline"
            href={m.url}
            key={i}
            target="_blank"
            rel="noopener noreferrer"
          >
            {m.title} ↗
          </a>
        ))}
        <p className="text-sm font-bold">
          {data.submission?.state.replaceAll('_', ' ') ?? 'Not started'}
        </p>
        <ul className="list-disc pl-5">
          {data.assignment.config.scheme.criteria.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
        <p className="text-sm text-slate2">
          {data.assignment.config.scheme.kind === 'points'
            ? `Marked out of ${data.assignment.config.scheme.maximum} points`
            : data.assignment.config.scheme.kind === 'levels'
              ? `Levels: ${data.assignment.config.scheme.levels?.join(', ')}`
              : 'Teacher feedback, without a numeric grade.'}
        </p>
      </header>
      {dirty && observed.current !== (data.submission?.version ?? 0) && (
        <section className="card-base" role="alert">
          <p>
            Your teacher changed this work while you were editing. Your local response is retained.
            Copy it before reloading the current version.
          </p>
          <button
            className="btn-pill-secondary"
            onClick={() => {
              if (window.confirm('Discard local changes and load the current saved version?')) {
                observed.current = data.submission?.version ?? 0;
                form.reset({
                  text: data.submission?.draft.text ?? '',
                  attachment_ids: data.submission?.draft.attachment_ids ?? [],
                  project_ids: data.submission?.draft.project_ids ?? [],
                  acknowledge: false,
                });
              }
            }}
          >
            Load current saved version
          </button>
        </section>
      )}
      {canEdit && (
        <form
          className="card-base space-y-5"
          onSubmit={form.handleSubmit((values) => write.mutate({ values, final: true }))}
          onChange={() => {
            key.current = crypto.randomUUID();
            setSaved('');
          }}
        >
          <h2 className="section-title">Your response</h2>
          <fieldset disabled={write.isPending || upload.isPending} className="min-w-0 space-y-5">
            {data.assignment.config.response_types.includes('text') && (
              <label className="block">
                <span className="field-label">Written response</span>
                <textarea
                  className="input-k12 min-h-48 min-w-0 max-w-full"
                  maxLength={12000}
                  {...form.register('text')}
                />
              </label>
            )}
            {data.assignment.config.response_types.includes('image') && (
              <section className="space-y-3">
                {photoWarning && (
                  <p role="status" className="text-sm text-slate2">
                    {photoWarning}
                  </p>
                )}
                <AssignmentPhotoPicker
                  onAdd={(file, rotation = 0) => upload.mutateAsync({ file, rotation })}
                  disabled={upload.isPending || write.isPending}
                />
                <p className="text-sm text-slate2">
                  Choose only images you intend to share. PDF, audio and other files are not
                  accepted by this editor.
                </p>
                {data.files.map((f) => (
                  <label className="flex items-center gap-2" key={f.id}>
                    <input type="checkbox" value={f.id} {...form.register('attachment_ids')} />
                    <span className="min-w-0 break-words">
                      {f.name} · {Math.ceil(f.size / 1024)} KB
                    </span>
                    <button
                      type="button"
                      className="btn-pill-ghost"
                      aria-label={`Preview ${f.name}`}
                      onClick={async () => {
                        try {
                          setPrivateImage(
                            await request(`/study/lms/assignments/${id}/images/${f.id}`),
                          );
                        } catch {
                          setSaved('This image is unavailable or access has changed.');
                        }
                      }}
                    >
                      Preview
                    </button>
                  </label>
                ))}
                <p className="text-xs text-slate2">
                  Checked photos are included. Uncheck to remove a photo from this response;
                  previously submitted versions retain their evidence.
                </p>
                {privateImage && (
                  <img
                    alt="Private response photo preview"
                    className="max-h-96 max-w-full rounded-2xl object-contain"
                    src={`data:${privateImage.mime};base64,${privateImage.data_base64}`}
                  />
                )}
              </section>
            )}
            {data.assignment.config.response_types.includes('project') && (
              <section className="space-y-3">
                <p className="field-label">Attach a saved project</p>
                {projects.isLoading && <p>Loading your projects…</p>}
                {projects.isError && (
                  <p role="alert">Could not load projects. Your written response remains saved.</p>
                )}
                {projects.data?.map((p) => (
                  <label key={p.id} className="flex gap-2">
                    <input type="checkbox" value={p.id} {...form.register('project_ids')} />
                    {p.title}
                  </label>
                ))}
                <p className="text-sm text-slate2">
                  Submission records the current saved version. Later project edits do not change
                  the evidence your teacher reviews.
                </p>
              </section>
            )}
            <label className="flex items-start gap-3 rounded-2xl bg-wash-sky p-4">
              <input type="checkbox" {...form.register('acknowledge')} />
              <span className="min-w-0">{data.visibility_notice}</span>
            </label>
            {form.formState.errors.acknowledge && (
              <p role="alert" className="text-brand-coral">
                Confirm who can see your submitted work before saving.
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="btn-pill-secondary"
                onClick={() =>
                  void form.handleSubmit((values) => write.mutate({ values, final: false }))()
                }
              >
                Save private draft
              </button>
              <button className="btn-pill-primary" type="submit">
                Submit work for review
              </button>
            </div>
          </fieldset>
          {(write.isPending || upload.isPending) && (
            <p role="status">{upload.isPending ? 'Uploading image…' : 'Saving your work…'}</p>
          )}
          {write.isError && (
            <p className="text-brand-coral" role="alert">
              {errorText(write.error)} Your draft is retained.
            </p>
          )}
          {upload.isError && (
            <p className="text-brand-coral" role="alert">
              {errorText(upload.error)} No submission was made.
            </p>
          )}
        </form>
      )}
      {data.excusal && (
        <section className="card-base">
          <h2 className="section-title">Excused by your teacher</h2>
          <p className="whitespace-pre-wrap">{data.excusal.reason}</p>
        </section>
      )}
      {saved && (
        <p role="status" className="rounded-2xl bg-wash-mint p-4">
          {saved}
        </p>
      )}
      {!canEdit && dirty && (
        <section className="card-base">
          <h2 className="section-title">Your unsaved local response</h2>
          <p>
            The assignment changed while you were editing. Copy your response before leaving; it has
            not been submitted.
          </p>
          <textarea
            className="input-k12 min-h-32"
            aria-label="Unsaved local response"
            readOnly
            value={form.watch('text')}
          />
        </section>
      )}
      {!canEdit && (
        <section className="card-base">
          <p>
            {data.submission?.state === 'excused'
              ? 'Your teacher excused this work.'
              : data.assignment.status === 'closed'
                ? 'Your teacher closed this assignment. Published feedback remains below.'
                : 'Your submitted version is saved. Your teacher must return it before you can revise it.'}
          </p>
        </section>
      )}
      {data.revisions.map((r) => (
        <article className="card-base space-y-4" key={r.id}>
          <h2 className="section-title">Submitted version {r.number}</h2>
          <p>
            {new Date(r.submitted_at).toLocaleString()}
            {r.late ? ' · Submitted after the deadline' : ''}
          </p>
          <p className="whitespace-pre-wrap">{r.body.text}</p>
          {r.body.attachment_ids?.map((file, i) => (
            <button
              className="btn-pill-secondary"
              key={file}
              onClick={async () => {
                try {
                  setImage({
                    ...(await request<{ mime: string; data_base64: string }>(
                      `/study/lms/assignments/${id}/images/${file}`,
                    )),
                    revisionId: r.id,
                  });
                } catch {
                  setSaved('This image is unavailable or access has changed.');
                }
              }}
            >
              View submitted image {i + 1}
            </button>
          ))}
          {image?.revisionId === r.id && (
            <img
              alt="Your submitted evidence"
              className="max-h-[600px] max-w-full rounded-2xl object-contain"
              src={`data:${image.mime};base64,${image.data_base64}`}
            />
          )}
          {r.body.project_versions?.map((p, i) => (
            <p key={i}>
              {p.title} · saved version {p.version}
            </p>
          ))}
          {r.reviews.map((v) => (
            <section className="rounded-2xl bg-wash-sky p-4 space-y-2" key={v.id}>
              <p className="font-bold">
                {v.action === 'returned'
                  ? 'Please revise your work'
                  : v.action === 'excused'
                    ? 'Excused by your teacher'
                    : 'Published teacher feedback'}{' '}
                · review {v.version}
              </p>
              {v.action === 'published' && (
                <p>
                  {v.result.points !== undefined
                    ? `${v.result.points} / ${v.scheme.maximum}`
                    : (v.result.level ?? 'Feedback only')}
                </p>
              )}
              <p className="whitespace-pre-wrap">{v.feedback}</p>
            </section>
          ))}
        </article>
      ))}
    </>
  );
}
